/* eslint consistent-return: "off" */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, Button } from "@chakra-ui/react";
import { exportBusinessTransactionsData } from "api/business-transactions";
import { IExportBusinessTransactionsPayload } from "api/business-transactions/types";
import { exportIndividualTransactionsData } from "api/individual-transactions";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useApp } from "contexts/app-context";
import {
  ALL_BUSINESS_TRANSACTION_STATUS_LIST,
  ALL_BUSINESS_TRANSACTION_TYPE_LIST,
} from "features/businesses/business-details/content/transactions/BusinessTransactionsTableHeaderCTAs";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import {
  useLocation,
  useParams,
  /* useNavigate, */
  useSearchParams,
} from "react-router-dom";
import { ROUTES } from "routes/constants";
import {
  capitalizeWords,
  formatDateToYYYYMMDD,
  handleDownloadBufferData,
  haveQueryParamsChanged,
} from "utils";
import {
  NO_EXPORT_DATA_FOUND,
  NO_NEW_FILTER_MESSAGE,
  SEARCH_DEBOUNCE_TIMER,
  START_INDEX,
  ZERO,
} from "utils/constants";

import {
  customErrorToast,
  customInfoToast,
  customSuccessToast,
  customWarningToast,
} from "ui/custom-toast";
import { IDropDownRef } from "ui/dropdown/types";
import { Export } from "ui/export";
import { IExportDateRangeValues, TExportType } from "ui/export/types";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import { TransactionsSubRouteName } from "../content";
import { TransactionsAnalytics } from "./TransactionsAnalytics";
import { DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS } from "./const";
import { ITableHeaderButtonsProps } from "./types";

const LG_WIDTH = 850;
const DEBOUNCE_TIMER = 50;
const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];

const MAX_BUSINESS_TRANSACTIONS_FILTER_OPTIONS = 3;
const TRANSACTION_TYPE_POSITION = 1;
const DEFAULT_FILTER_OPTIONS: TFilterOption[] = [
  {
    type: "checkbox",
    title: "Status",
    key: "status",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Successful",
        value: "successful",
      },
      {
        label: "Pending",
        value: "pending",
      },
      {
        label: "Failed",
        value: "failed",
      },
    ],
  },
  {
    type: "date-range",
    title: "Custom Date Range",
    key: "dateRange",
  },
];

export const TableHeaderButtons = memo((props: ITableHeaderButtonsProps) => {
  const {
    handleGetTransactionsData,
    isLoading,
    isRefreshing,
    setIsRefreshing,
    isLoadingTransactionAnalytics,
    transactionAnalytics,
  } = props;
  // const navigate = useNavigate();
  const { transactionType } = useParams<{
    transactionType: TransactionsSubRouteName;
  }>();
  const location = useLocation();
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const type = searchParams.get("type");
  const isDateFilter = searchParams.get("isDateFilter");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const updateQueryParams = useUpdateQueryParams();
  const { isFilterError, setIsFilterError, filterRetryCountParams } =
    useFilterQueryParams({
      apiFilterRoute:
        transactionType === TransactionsSubRouteName.TRANSFERS
          ? "/control/transactions/business/transfer"
          : "/control/transactions/business/bill-payment",
    });
  const [filterValues, setFilterValues] = useState<TFilterValues>({
    status: status ? status.split(",") : [],
    transactionType: type ? type.split(",") : [],
    dateRange: {
      startDate: isDateFilter === "true" ? null : startDate,
      endDate: isDateFilter === "true" ? null : endDate,
    },
  });
  const filterRef = useRef<IFilterRef | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { tableHeaderCTAWidth, handleUpdateTableHeaderCTAWidth } = useApp();

  const debouncedUpdateWidth = debounce(() => {
    if (wrapperRef.current && wrapperRef.current.offsetWidth !== START_INDEX) {
      const newWidth = wrapperRef.current.offsetWidth;
      if (
        (newWidth <= LG_WIDTH && tableHeaderCTAWidth <= LG_WIDTH) ||
        (newWidth > LG_WIDTH && tableHeaderCTAWidth > LG_WIDTH)
      ) {
        return;
      }
      const newElementWidth = wrapperRef.current.offsetWidth;
      handleUpdateTableHeaderCTAWidth(newElementWidth);
    }
  }, DEBOUNCE_TIMER);

  const isFilterDisabled = !(
    (transactionType === TransactionsSubRouteName.TRANSFERS &&
      (filterValues?.transactionType as string[])?.length > ZERO) ||
    (filterValues?.status as string[])?.length > ZERO ||
    (filterValues?.dateRange as IDateRangeValues)?.startDate
  );

  const filterOptions: TFilterOption[] = useMemo(() => {
    if (
      !transactionType ||
      transactionType === TransactionsSubRouteName.BILLS_PAYMENTS
    ) {
      return DEFAULT_FILTER_OPTIONS.filter(
        (option) => option.key !== "transactionType"
      );
    }

    if (
      DEFAULT_FILTER_OPTIONS.length === MAX_BUSINESS_TRANSACTIONS_FILTER_OPTIONS
    ) {
      return DEFAULT_FILTER_OPTIONS;
    }

    const transactionTypeFilterOption: TFilterOption = {
      type: "checkbox",
      title: "Transaction Type",
      key: "transactionType",
      options: [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Credit",
          value: "credit",
        },
        {
          label: "Debit",
          value: "debit",
        },
      ],
    };

    DEFAULT_FILTER_OPTIONS.splice(
      TRANSACTION_TYPE_POSITION,
      START_INDEX,
      transactionTypeFilterOption
    );
    return DEFAULT_FILTER_OPTIONS;
  }, [transactionType]);

  const handleResetFilter = () => {
    setFilterValues({
      status: [],
      transactionType: [],
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });
  };

  const handleRefreshTable = async () => {
    setIsRefreshing(true);
    setSearchQuery("");
    await handleGetTransactionsData({
      ...DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS,
    });
    setIsRefreshing(false);
  };

  const onTransactionsSearch = useCallback(
    debounce(async (value: string) => {
      if (value.trim() === "") {
        updateQueryParams({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          search: null,
          page: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page),
          limit: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit),
          isDateFilter: null,
          resetFilter: "normal",
        });
        return;
      }

      updateQueryParams({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        search: value,
        page: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page),
        limit: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit),
        isDateFilter: null,
        resetFilter: "normal",
      });
    }, SEARCH_DEBOUNCE_TIMER),
    [location]
  );

  const handleSearchQueryChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = evt.target;
    setSearchQuery(value);
    onTransactionsSearch(value);
  };

  const handleFilterValueChange = (key: string, value: TFilterValue) => {
    let newValue = value;
    if (key === "status") {
      const transactionStatuses = value as string[];
      const currentTransactionStatus = filterValues?.status as string[];
      if (
        transactionStatuses.includes("all") &&
        currentTransactionStatus.length <
          ALL_BUSINESS_TRANSACTION_STATUS_LIST.length
      ) {
        newValue = ALL_BUSINESS_TRANSACTION_STATUS_LIST;
      } else {
        newValue = transactionStatuses.filter(
          (currentStatus) => currentStatus !== "all"
        );
      }
    } else if (key === "transactionType") {
      const transactionType = value as string[];
      const currentTransactionType = filterValues?.transactionType as string[];
      if (
        transactionType.includes("all") &&
        currentTransactionType.length <
          ALL_BUSINESS_TRANSACTION_TYPE_LIST.length
      ) {
        newValue = ALL_BUSINESS_TRANSACTION_TYPE_LIST;
      } else {
        newValue = transactionType.filter((trxType) => trxType !== "all");
      }
    }
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleApplyFilter = () => {
    const newQueryParams: Record<string, string | null> = {};
    const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
    const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
    const status = filterValues.status;
    const type = filterValues.transactionType;

    if (startDate) {
      newQueryParams["startDate"] = startDate;
    } else {
      newQueryParams["startDate"] = null;
    }

    if (endDate) {
      newQueryParams["endDate"] = endDate;
    } else {
      newQueryParams["endDate"] = null;
    }

    if (Array.isArray(status) && status.length > ZERO) {
      newQueryParams["status"] = status.join(",");
    } else {
      newQueryParams["status"] = null;
    }

    if (Array.isArray(type) && type.length > ZERO) {
      newQueryParams["type"] = type.join(",");
    } else {
      newQueryParams["type"] = null;
    }

    newQueryParams["page"] =
      DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page.toString();
    newQueryParams["limit"] =
      DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit.toString();

    setIsFilterError(false);
    updateQueryParams({
      ...Object.fromEntries(new URLSearchParams(location.search)),
      ...newQueryParams,
      resetFilter: "normal",
      isDateFilter: null,
      ...filterRetryCountParams,
    });

    if (
      haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: newQueryParams,
      }) ||
      isFilterError
    ) {
      setIsFiltering(true);
    } else {
      customWarningToast({
        message: NO_NEW_FILTER_MESSAGE,
      });
    }
  };

  const dropDownRef = useRef<IDropDownRef | null>(null);
  const [dateRange, setDateRange] = useState<IExportDateRangeValues>({
    startDate: null,
    endDate: null,
  });
  const [exportType, setExportType] = useState<TExportType | "">("");

  const handleExportTypeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setExportType(value as TExportType);
  };

  const handleDateRangeChange = (name: string, value: Date | null) => {
    if (!value) return;
    setDateRange({
      ...dateRange,
      [name]: value,
    });
  };

  const handleResetExport = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
    setExportType("");
  };

  const handleExport = async () => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate || !exportType || !transactionType) return;
    const formattedStartDate = formatDateToYYYYMMDD(startDate);
    const formattedEndDate = formatDateToYYYYMMDD(endDate);
    const individualsPathName = ROUTES.INDIVIDUALS_TRANSACTIONS_PAGE.replace(
      ":transactionType",
      ""
    );
    const isIndividuals = location.pathname.includes(individualsPathName);
    setIsExporting(true);
    const exportPayload: IExportBusinessTransactionsPayload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      format: exportType,
      transactionType:
        transactionType === TransactionsSubRouteName.TRANSFERS
          ? "transfer"
          : "bill_payment",
    };
    const exportFunction = isIndividuals
      ? exportIndividualTransactionsData
      : exportBusinessTransactionsData;
    try {
      const { data, error, message } = await exportFunction(exportPayload);
      if (error) {
        customErrorToast({ message });
      } else if (data?.data) {
        const exportData = data?.data;
        if (exportData.length > ZERO) {
          handleDownloadBufferData({
            data: exportData,
            name: `${isIndividuals ? "Individuals" : "Business"}-${
              transactionType === TransactionsSubRouteName.TRANSFERS
                ? "Transfers"
                : "Bills_Payments"
            }-${formattedStartDate}-${formattedEndDate}`,
            type: exportType,
          });
          customSuccessToast({ message: capitalizeWords(message) });
          handleResetExport();
          if (dropDownRef.current) {
            dropDownRef.current.handleToggleDropdown();
          }
        } else {
          customInfoToast({
            message: NO_EXPORT_DATA_FOUND,
          });
        }
      } else {
        customErrorToast({
          message: "No data returned",
        });
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsExporting(false);
  };

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver(() => {
      debouncedUpdateWidth();
    });
    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [tableHeaderCTAWidth]);

  useEffect(() => {
    if (!isLoading && filterRef?.current?.isFilterOpen) {
      setIsFiltering(false);
      filterRef?.current?.handleToggleFilter();
    }
  }, [isLoading, filterRef]);

  useEffect(() => {
    if (!isLoading && isRefreshing) {
      handleResetFilter();
      setIsRefreshing(false);
    }
  }, [isLoading]);

  return (
    <Box
      className={`flex px-3 ${tableHeaderCTAWidth < LG_WIDTH ? "flex-col gap-4 items-start h-[185px] justify-center" : "items-center justify-between h-[85px]"}`}
      ref={wrapperRef}
    >
      <Box
        className={`flex ${tableHeaderCTAWidth < LG_WIDTH ? "flex-col justify-evenly items-start" : "items-center"} gap-4`}
      >
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
          placeholder="Search by description or reference"
        />
        <Box className="flex items-center gap-4">
          <Filter
            options={filterOptions}
            values={filterValues}
            onChange={handleFilterValueChange}
            onApplyFilter={handleApplyFilter}
            filterBtnCss={tableHeaderCtaCss}
            isLoading={isFiltering}
            ref={filterRef}
            isApplyBtnDisabled={isFilterDisabled}
            handleResetFilter={handleResetFilter}
          />
          <Export
            isLoading={isExporting}
            handleExport={handleExport}
            dateRange={dateRange}
            exportType={exportType}
            handleExportTypeChange={handleExportTypeChange}
            handleDateRangeChange={handleDateRangeChange}
            dropDownRef={dropDownRef}
            availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
            handleResetExport={handleResetExport}
          />
          <Button
            css={tableHeaderCtaCss}
            onClick={!isRefreshing ? handleRefreshTable : undefined}
            disabled={isRefreshing}
          >
            <RefreshIcon
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            <p className="text-sm text-[#374151] font-semibold">Refresh</p>
          </Button>
        </Box>
      </Box>
      <Box>
        <TransactionsAnalytics
          analytics={transactionAnalytics}
          isLoadingTransactionAnalytics={isLoadingTransactionAnalytics}
          transactionType={
            (transactionType ||
              TransactionsSubRouteName.TRANSFERS) as TransactionsSubRouteName
          }
        />
      </Box>
    </Box>
  );
});
